import { Material, PartItem } from '@cutr/constants/cutlist';
import cn from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '@/api/backend';
import { useCutlistState } from '@/api/store';
import { onFocusSelect } from '@/hooks';
import { Checkbox } from '@/primitives/Checkbox';
import { Dropdown, DropdownOption } from '@/primitives/Dropdown';
import { Alert, Dashboard, Icon, Loader } from '@/primitives/Icons';
import { Input } from '@/primitives/Input';
import { useCurrentFeatures } from '@/theme';
import { useDebounce } from '@/utils/hooks';
import { cleanNumberInput, convertMMToUM, convertUMToMM } from '@/utils/input';

import styles from '../PartTableCommon/styles.module.css';
import { TableRowActions } from '../PartTableCommon/TableRowActions';
import { TableRowProps } from '../PartTableCommon/types';

export const TableRowBeam = ({
  part,
  hasError,
  onPartUpdate,
  onPartDelete,
  isFieldValid,
  validateNumericInput,
}: TableRowProps) => {
  const { t } = useTranslation();
  const { partLabelMaxLength } = useCurrentFeatures();

  const { setPart } = useCutlistState();

  const [beamMaterials, setBeamMaterials] = useState<Material[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const debouncedWidthMM = useDebounce(part.widthMM, 1000, 0);
  const debouncedThickness = useDebounce(part.thickness, 1000, 0);

  const unavailableBeamMaterial = useMemo(() => {
    return (
      debouncedWidthMM &&
      debouncedThickness &&
      !beamMaterials.length &&
      !isLoading
    );
  }, [beamMaterials, debouncedWidthMM, debouncedThickness, isLoading]);

  useEffect(() => {
    if (debouncedWidthMM && debouncedThickness) {
      setIsLoading(true);
      api
        .beamMaterials({
          profileWidthMM: debouncedWidthMM,
          profileThicknessMM: convertUMToMM(debouncedThickness),
        })
        .then((materials: Material[]) => {
          setBeamMaterials(materials || []);

          const partUpdate: PartItem = {
            ...part,
            beamMaterialId: null,
          };

          if (materials.length) {
            const material =
              materials.find(
                (material) => material.lengthMM === part.lengthMM
              ) || materials[0];

            partUpdate.beamMaterialId = material.id;
            partUpdate.lengthMM = material.lengthMM;
          }

          setPart(partUpdate);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setBeamMaterials([]);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedWidthMM, debouncedThickness]);

  return (
    <tr
      id={`beamRow-${part.id}`}
      className={hasError ? styles.errors : undefined}
    >
      <td>
        {
          <span className="flexAlign gap-xs">
            <Icon
              className={cn(styles.unflex, 'tooltip')}
              data-tooltip={t('cutlist-form.panel')}
              icon={<Dashboard />}
              color="var(--gray-7)"
            />
            <Input
              className="snug"
              defaultValue={part.label}
              maxLength={partLabelMaxLength}
              placeholder={t('cutlist-form.field.partName.placeholder')}
              name={`label_${part.id}`}
              key={`label_${part.id}`}
              onFocus={onFocusSelect}
              data-1p-ignore
              autoComplete="off"
              onChange={(e) => {
                onPartUpdate('label', e.currentTarget.value);
              }}
            />
          </span>
        }
      </td>
      <td className={styles.fixedMaterialSizeColumn}>
        <Input
          className={isFieldValid('quantity') ? 'snug' : 'snug invalid'}
          type="number"
          value={part.quantity}
          name={`quantity_${part.id}`}
          key={`quantity_${part.id}`}
          data-1p-ignore
          autoComplete="off"
          onInput={(e) => {
            onPartUpdate(
              'quantity',
              validateNumericInput('quantity', e.currentTarget.value)
            );
          }}
        />
      </td>

      <td className={styles.fixedMaterialSizeColumn}>
        <Input
          className={`snug ${unavailableBeamMaterial ? 'warningWithIcon' : ''}`}
          value={convertUMToMM(part.thickness)}
          placeholder={t('cutlist-form.field.thickness.placeholder')}
          pattern="[0-9]+"
          name={`thickness_${part.id}`}
          key={`thickness_${part.id}`}
          data-1p-ignore
          autoComplete="off"
          icon={
            unavailableBeamMaterial ? (
              <Icon icon={<Alert />} color="var(--amber-6)" />
            ) : undefined
          }
          onInput={(e) => {
            onPartUpdate(
              'thickness',
              getThicknessUMFromInputMM(e.currentTarget.value)
            );
          }}
        />
      </td>
      <td className={styles.fixedMaterialSizeColumn}>
        <Input
          className={`snug ${unavailableBeamMaterial ? 'warningWithIcon' : ''}`}
          value={part.widthMM}
          placeholder={t('cutlist-form.field.width.placeholder')}
          pattern="[0-9]+"
          name={`width_${part.id}`}
          key={`width_${part.id}`}
          data-1p-ignore
          autoComplete="off"
          icon={
            unavailableBeamMaterial ? (
              <Icon icon={<Alert />} color="var(--amber-6)" />
            ) : undefined
          }
          onInput={(e) => {
            onPartUpdate('widthMM', cleanNumberInput(e.currentTarget.value));
          }}
        />
      </td>

      <td className={styles.fixedMaterialSizeColumn}>
        {!beamMaterials.length ||
        !beamMaterials.some(
          (material) => material.lengthMM === part.lengthMM
        ) ? (
          <Input
            className="snug"
            value={part.lengthMM}
            placeholder={t('cutlist-form.field.length.placeholder')}
            pattern="[0-9]+"
            name={`length_${part.id}`}
            key={`length_${part.id}`}
            data-1p-ignore
            autoComplete="off"
            onInput={(e) => {
              setPart({
                ...part,
                beamMaterialId: null,
                lengthMM: Number(cleanNumberInput(e.currentTarget.value)),
              });
            }}
          />
        ) : isLoading ? (
          <Icon icon={<Loader />} />
        ) : (
          <Dropdown
            id={`beamMaterials-${part.id}`}
            value={String(part.lengthMM) || ''}
            options={beamMaterials.map(
              (material) =>
                ({
                  label: material.lengthMM,
                  value: String(material.lengthMM),
                  component: () => <span>{material.lengthMM}</span>,
                }) as DropdownOption
            )}
            onSelected={(lengthValue) => {
              const selectedMaterial = beamMaterials.find(
                (m) => String(m.lengthMM) === lengthValue
              );
              if (selectedMaterial) {
                setPart({
                  ...part,
                  beamMaterialId: selectedMaterial.id,
                  lengthMM: selectedMaterial.lengthMM,
                });
              }
            }}
          />
        )}
      </td>

      <td className={styles.fixedMaterialSizeColumn}>
        <Checkbox
          checked={part.beamRounded}
          onChange={(e) => onPartUpdate('beamRounded', e.target.checked)}
        />
      </td>
      <td style={{ '--spaceX': '0.1rem' } as React.CSSProperties}>
        <TableRowActions part={part} onPartDelete={onPartDelete} />
      </td>
    </tr>
  );
};

const getThicknessUMFromInputMM = (value: string): number => {
  const cleanValue = cleanNumberInput(value);
  if (!cleanValue) {
    return 0;
  }
  return cleanValue > 0 ? convertMMToUM(cleanValue) : 0;
};
